import type { ShotListRowDict } from '../../../../generated/nodeDataTypes'

const normalized = (value: string) => value.normalize('NFKC').trim().toLowerCase().replace(/\s+/gu, ' ').replace(/[\s:.。]+$/gu, '')

/** Older plans still store a separate movement field; still-image prompts must remain untouched. */
export function cameraDescriptionIsRedundant(description: string, cameraMovement: string): boolean {
  const phrase = normalized(cameraMovement)
  if (!phrase) return true
  const text = normalized(description)
  // CJK prose commonly attaches particles without spaces; Latin words still
  // need boundaries so "Static" does not match inside "ecstatic".
  if (/[\p{Script=Han}\p{Script=Hangul}\p{Script=Hiragana}\p{Script=Katakana}]/u.test(phrase)) return text.includes(phrase)
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}(?=$|[^\\p{L}\\p{N}])`, 'u').test(text)
}

export type OutgoingTransition = { label: string; kind: 'hard-cut' | 'continuous' | 'named'; uncut: boolean; invalidBoundary: boolean }

/** Only outgoing seams are shown; missing scene metadata is intentionally neutral. */
export function outgoingTransition(row: ShotListRowDict, next?: ShotListRowDict): OutgoingTransition {
  const value = row.transition_out?.trim() ?? ''
  const token = normalized(value)
  if (!token || ['cut', 'cut to', 'hard cut'].includes(token)) {
    return { label: '', kind: 'hard-cut', uncut: false, invalidBoundary: false }
  }
  if (token === 'continuous') {
    const invalidBoundary = !next || (row.scene_ordinal !== undefined && next.scene_ordinal !== undefined && row.scene_ordinal !== next.scene_ordinal)
    return {
      label: 'Continuous', kind: 'continuous', invalidBoundary,
      uncut: !!next && row.scene_ordinal !== undefined && row.scene_ordinal === next.scene_ordinal,
    }
  }
  return { label: value, kind: 'named', uncut: false, invalidBoundary: false }
}

export function openingTransition(rows: readonly ShotListRowDict[]): string | undefined {
  if (!rows.length) return undefined
  const entry = rows[0].transition_in?.trim() ?? ''
  return ['', 'cut', 'cut to', 'hard cut'].includes(normalized(entry)) ? undefined : entry
}
